/**
 * Careers utility functions
 *
 * @package Interact_26
 */

import { type PinpointApiPosting, type PinpointJob, type CareersConfig } from './types';

/**
 * Normalize a raw posting into the flat PinpointJob shape.
 */
export const normalizePosting = (raw: PinpointApiPosting): PinpointJob => ({
  // The API sends string ids, manual JSON may use numbers — string everywhere.
  id: String(raw.id),
  title: raw.title || '',
  department: raw.job?.department?.name || '',
  employment_type: raw.employment_type_text || raw.employment_type || '',
  location: raw.location?.name || raw.location?.city || '',
  salary: raw.compensation || '',
  description: raw.description || '',
  responsibilities: raw.key_responsibilities || '',
  skills: raw.skills_knowledge_expertise || '',
  benefits: raw.benefits || '',
  url: raw.url || '',
});

/**
 * Convert a string to a URL-safe slug.
 */
export const slugify = (text: string): string =>
  text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_]+/g, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Map a raw Pinpoint department name to the canonical department label
 * using the mapping provided in the config.
 */
export const getMappedDepartment = (rawDepartment: string, config: CareersConfig): string => {
  const mapping = config.departmentMapping;
  const lower = rawDepartment.toLowerCase().trim();

  for (const [key, value] of Object.entries(mapping)) {
    if (key.toLowerCase() === lower) {
      return value;
    }
  }

  return rawDepartment;
};

/**
 * Check whether a job matches the configured department filter.
 *
 * Uses departmentGrouping to include sub-departments under a parent
 * (e.g. "Engineering" also includes "Internal Operations").
 * Returns true if no filter is set (show all).
 */
export const matchesDepartmentFilter = (job: PinpointJob, config: CareersConfig): boolean => {
  const filter = config.departmentFilter;

  if (!filter) {
    return true;
  }

  const mapped = getMappedDepartment(job.department, config);
  const filterLower = filter.toLowerCase();

  // Check department grouping (e.g. Engineering includes Internal Operations)
  const grouping = config.departmentGrouping?.[filter];
  if (grouping) {
    if (grouping.some((dept) => dept.toLowerCase() === mapped.toLowerCase())) {
      return true;
    }
  }

  // Exact match on mapped department name
  if (mapped.toLowerCase() === filterLower) {
    return true;
  }

  return false;
};

const LEGACY_NUMERIC_ID = /-(\d{5,})$/;

// Stands in for a missing department; an empty segment would not route back.
const DEPARTMENTLESS_SLUG = 'jobs';

/**
 * Build a job's canonical URL.
 */
export const jobUrl = (job: PinpointJob, config: CareersConfig): string => {
  const department = slugify(getMappedDepartment(job.department, config));

  return `${config.careersPageUrl}${department || DEPARTMENTLESS_SLUG}/${job.id}`;
};

/**
 * Resolve a URL segment to a job, accepting the Pinpoint-era forms too.
 *
 * Those URLs were "{title-slug}-{numeric-id}" and carried the id in the
 * suffix, whereas a Workday slug may itself end in a requisition number — so
 * a trailing number is tried both ways rather than assumed to be either, and
 * the remainder is finally matched on title, for a job that changed vendor
 * keeping its name but not its id.
 */
export const findJobBySlug = (jobs: PinpointJob[], slug: string): PinpointJob | undefined => {
  const exact = jobs.find((job) => job.id === slug);

  if (exact) {
    return exact;
  }

  const trailing = slug.match(LEGACY_NUMERIC_ID);

  if (!trailing) {
    return undefined;
  }

  const stripped = slug.slice(0, -trailing[0].length);

  return (
    jobs.find((job) => job.id === trailing[1]) ??
    jobs.find((job) => job.id === stripped) ??
    jobs.find((job) => slugify(job.title) === stripped)
  );
};

/**
 * Curated chip order first, then any department a live vacancy uses that is
 * not in the config yet. Sub-departments folded into a parent by
 * departmentGrouping stay hidden, as they are on purpose.
 */
export const buildFilterDepartments = (jobs: PinpointJob[], config: CareersConfig): string[] => {
  const curated = config.filterDepartments ?? [];
  const grouped = new Set(Object.values(config.departmentGrouping ?? {}).flat());
  const extra = new Set<string>();

  for (const job of jobs) {
    const department = getMappedDepartment(job.department, config);

    if (department && !curated.includes(department) && !grouped.has(department)) {
      extra.add(department);
    }
  }

  return [...curated, ...extra];
};

/**
 * Escape HTML special characters to prevent XSS.
 */
export const escapeHtml = (text: string): string => {
  const div = document.createElement('div');
  div.appendChild(document.createTextNode(text));
  return div.innerHTML;
};

/**
 * Escape a string for use in an HTML attribute.
 */
export const escapeAttr = (text: string): string =>
  text
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

/**
 * Sanitize HTML from external sources by stripping dangerous elements
 * and event handler attributes. Preserves safe formatting tags.
 */
export const sanitizeHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  // Remove dangerous elements
  doc
    .querySelectorAll('script, style, iframe, object, embed, form, input, textarea, select, button')
    .forEach((el) => el.remove());

  // Remove event handler attributes and javascript: URLs
  doc.querySelectorAll('*').forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      if (attr.name.startsWith('on') || attr.name === 'srcdoc') {
        el.removeAttribute(attr.name);
      }
      if (
        ['href', 'src', 'action'].includes(attr.name) &&
        attr.value.trim().toLowerCase().startsWith('javascript:')
      ) {
        el.removeAttribute(attr.name);
      }
    }
  });

  return doc.body.innerHTML;
};
